import React, { useEffect, useRef, useState } from "react";
import { Modal, Form, Button, Space, Tooltip, Typography, message } from "antd";
import Alert from "./GxAlert";
import { LeftOutlined, RightOutlined, SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";

const { Text } = Typography;

/**
 * ویرایشگرِ نفر-به-نفرِ قابل‌استفادهٔ مجدد (مثل نرم‌افزار حسابداری):
 * کادرِ بالا با نام/کد + «نفر X از Y» + قبلی/بعدی، فرمِ ویرایش، پیش‌نمایش، و دکمه‌های ذخیره.
 * هنگام جابه‌جایی اگر تغییرِ ذخیره‌نشده باشد می‌پرسد «ذخیره شود؟»؛ اگر تغییری نبود بی‌سؤال جابه‌جا می‌شود.
 *
 * props:
 *  - open, onClose
 *  - items: آرایهٔ رکوردها به همان ترتیبِ نمایشِ لیست
 *  - initialIndex: ایندکسِ شروع (رکوردی که کاربر کلیک کرد)
 *  - form: نمونهٔ Form از والد (Form.useForm)
 *  - title: عنوان مودال
 *  - warning: متنِ Alert بالای فرم (اختیاری)
 *  - headerTitle(item) / headerSubtitle(item) / headerTag(item)
 *  - loadValues(item) => مقادیرِ فرم
 *  - renderFields(item) => Form.Itemها
 *  - renderPreview(values, item) => ReactNode (اختیاری)
 *  - onSave(item, changedValues, allValues) => Promise<boolean>
 *      فقط فیلدهای **تغییرکرده** فرستاده می‌شوند — وگرنه ناخالصِ کهنهٔ فرم روی عددِ
 *      تازه‌ای که ردیف‌های اصلاحی روی سرور ساخته‌اند می‌نشست.
 *  - onItemChange(item) => void  (هر بار نفرِ جاری عوض شد — برای بارگذاریِ جزئیاتِ او)
 *  - isExtraDirty() => bool / saveExtra(item) => Promise<boolean>
 *      بخشِ خارج از فرم (مثلاً جدولِ ردیف‌های اصلاحی) — قبل از فیلدها ذخیره می‌شود.
 *  - width
 */
export default function RecordEditorModal({
  open, onClose, items = [], initialIndex = 0, form,
  title = "ویرایش", warning,
  headerTitle, headerSubtitle, headerTag,
  loadValues, renderFields, renderPreview,
  onSave, onItemChange, width = 640,
  isExtraDirty, saveExtra,
}) {
  const [idx, setIdx] = useState(initialIndex);
  const [loaded, setLoaded] = useState({});
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  // آخرین لیست — بعد از ذخیره (که والد لیست را تازه می‌کند) از همین خوانده می‌شود،
  // نه از closureِ کهنهٔ لحظهٔ کلیک.
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const idxRef = useRef(idx);

  const total = items.length;
  const item = items[idx];

  const applyItem = (i) => {
    const it = itemsRef.current[i];
    if (!it) return;
    const v = loadValues(it);
    form.setFieldsValue(v);
    setLoaded(v); setVals(v); setIdx(i); idxRef.current = i;
    // جزئیاتِ نفرِ تازه را والد می‌خواند (حضور، کارمزد، بیمه، ردیف‌های اصلاحی)
    if (typeof onItemChange === "function") onItemChange(it);
  };

  // بارگذاری هنگام بازشدنِ مودال
  useEffect(() => {
    if (open) applyItem(initialIndex);
  }, [open]); // فقط هنگام باز/بسته‌شدن؛ ناوبری داخلی با applyItem انجام می‌شود

  const changedFields = () => {
    const cur = form.getFieldsValue();
    const out = {};
    Object.keys(loaded).forEach((k) => {
      // فیلدی که در فرم رندر نشده (undefined) «تغییر» نیست — وگرنه PATCHِ خالی می‌رفت
      if (cur[k] === undefined) return;
      if (JSON.stringify(cur[k] ?? null) !== JSON.stringify(loaded[k] ?? null)) out[k] = cur[k];
    });
    return out;
  };
  const formDirty = () => Object.keys(changedFields()).length > 0;
  const extraDirty = () => (typeof isExtraDirty === "function" ? !!isExtraDirty() : false);
  const isDirty = () => formDirty() || extraDirty();

  const save = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return false; }
    const fDirty = formDirty();
    const xDirty = extraDirty();
    if (!fDirty && !xDirty) { message.info("تغییری برای ذخیره نیست"); return true; }
    setBusy(true);
    let ok = true;
    try {
      // اول بخشِ خارج از فرم (ردیف‌ها ناخالص/خالص را روی سرور عوض می‌کنند)،
      // بعد فقط فیلدهای تغییرکرده.
      if (xDirty && typeof saveExtra === "function") ok = await saveExtra(item);
      if (ok && fDirty) ok = await onSave(item, changedFields(), v);
    } finally { setBusy(false); }
    if (ok) {
      // لیستِ والد تازه شده؛ همان نفر را (با id، نه ایندکس — ترتیب ممکن است عوض شود) دوباره بار کن
      const j = itemsRef.current.findIndex((x) => x.id === item.id);
      applyItem(j >= 0 ? j : idx);
    }
    return ok;
  };

  const confirmThenMove = (action) => {
    Modal.confirm({
      title: "تغییراتِ ذخیره‌نشده",
      content: `تغییراتِ «${headerTitle ? headerTitle(item) : ""}» هنوز ذخیره نشده. ذخیره شود؟`,
      okText: "ذخیره و برو", cancelText: "بدون ذخیره، برو",
      keyboard: false, maskClosable: false,
      onOk: async () => { const ok = await save(); if (ok) action(); },
      onCancel: action,
    });
  };

  const go = (delta) => {
    const t = idx + delta;
    if (t < 0 || t >= total || !item) return;
    if (isDirty()) confirmThenMove(() => applyItem(t));
    else applyItem(t);
  };

  const saveAndNext = async () => {
    const ok = await save();
    const cur = idxRef.current;
    if (ok && cur < itemsRef.current.length - 1) applyItem(cur + 1);
  };

  const handleClose = () => {
    if (item && isDirty()) {
      Modal.confirm({
        title: "تغییراتِ ذخیره‌نشده",
        content: "قبل از بستن، تغییرات ذخیره شود؟",
        okText: "ذخیره و ببند", cancelText: "بدون ذخیره ببند",
        keyboard: false, maskClosable: false,
        onOk: async () => { const ok = await save(); if (ok) onClose(); },
        onCancel: onClose,
      });
    } else onClose();
  };

  // میان‌بر صفحه‌کلید: Alt+← بعدی، Alt+→ قبلی (RTL)
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); go(1); }
      if (e.key === "ArrowRight") { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  return (
    <Modal open={open} onCancel={handleClose} width={width} footer={null} title={title}
      maskClosable={false}>
      {item && (
        <>
          {/* کادرِ هدر: نام/کد + قبلی/بعدی + شمارنده */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, flexWrap: "wrap",
            background: "var(--surface-2,#f6f8fb)", border: "1px solid var(--border,#e4e8f0)",
            borderRadius: 12, padding: "12px 14px", marginBottom: 14,
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 800, fontSize: 16 }}>
                  {headerTitle ? headerTitle(item) : ""}
                </span>
                {headerTag && headerTag(item)}
              </div>
              {headerSubtitle && (
                <Text type="secondary" style={{ fontSize: 12.5 }}>{headerSubtitle(item)}</Text>
              )}
            </div>
            <Space size={6}>
              <Tooltip title="نفر قبلی (Alt+→)">
                <Button icon={<RightOutlined />} onClick={() => go(-1)} disabled={idx <= 0}>قبلی</Button>
              </Tooltip>
              <Text strong style={{ minWidth: 62, textAlign: "center", whiteSpace: "nowrap" }}>
                {`${idx + 1} از ${total}`}
              </Text>
              <Tooltip title="نفر بعدی (Alt+←)">
                <Button icon={<LeftOutlined />} onClick={() => go(1)} disabled={idx >= total - 1}>بعدی</Button>
              </Tooltip>
            </Space>
          </div>

          {warning && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={warning} />}

          <Form form={form} layout="vertical" style={{ direction: "rtl" }}
            onValuesChange={(_, all) => setVals(all)}>
            {renderFields(item)}
          </Form>

          {renderPreview && (
            <div style={{
              marginTop: 2, marginBottom: 14, padding: "10px 14px", borderRadius: 10,
              background: "var(--success-soft,#e7f4ec)", border: "1px solid var(--border,#e4e8f0)",
            }}>
              {renderPreview(vals, item)}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <Button onClick={handleClose}>بستن</Button>
            <Space wrap>
              <Button icon={<SaveOutlined />} onClick={save} loading={busy}>ذخیره</Button>
              <Button type="primary" icon={<ArrowLeftOutlined />} onClick={saveAndNext}
                loading={busy} disabled={idx >= total - 1}>ذخیره و بعدی</Button>
            </Space>
          </div>
        </>
      )}
    </Modal>
  );
}
